import React, { useRef, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import SecondaryListingDatePicker from './date-picker/date-picker';
import OTPFormModal from './otp-form';
import ConfirmReserveModal from './confirm-reserve-modal';
import { nonInstitutionEmail } from '../../helpr/nonInstitutionEmail';
import ListingCalendarService from '../../services/listing-calendar-service';
import useCaptcha from '../../hooks/use-captcha';

export const ReservationModal = ({
  show,
  onHide,
  primaryListingDate,
  onConfirm,
  reservedDates = [],
}) => {
  // instantiate service once
  const listingCalendarService = React.useMemo(() => new ListingCalendarService(), []);

  // form state
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [issuerCode, setIssuerCode] = useState('');
  const [secondaryListingDate, setSecondaryListingDate] = useState('');
  const [showsecondaryListingDate, setShowsecondaryListingDate] = useState(true);
  const [showCalendar2, setShowCalendar2] = useState(false);
  const [secondaryListingDateError, setSecondaryListingDateError] = useState('');

  const pendingFormRef = useRef(null);
  const isCaptchaFlowRef = useRef(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formData, setFormData] = useState(null);
  const [reservationPayloadForConfirm, setReservationPayloadForConfirm] = useState(null);

  const [reservationError, setReservationError] = useState('');
  const [captchaError, setCaptchaError] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [previousModal, setPreviousModal] = useState(null);

  const [otpInitialCooldown, setOtpInitialCooldown] = useState(120);
  const [otpInitialError, setOtpInitialError] = useState('');
  const [otpInitialInfo, setOtpInitialInfo] = useState('');

  const captchaScriptUrl = process.env.REACT_APP_REPORTS_LISTING_CALENDAR_CAPTCHA;
  const encryptedApiKey = process.env.REACT_APP_REPORTS_LISTING_CALENDAR_CAPTCHA_API_KEY;

  const {
    captchaRef,
    showCaptcha,
    setShowCaptcha,
    renderCaptcha,
    cleanupCaptcha,
    captchaToken,
  } = useCaptcha({ captchaScriptUrl, encryptedApiKey });

  // Guard to prevent duplicate in-flight requests (double-clicks, re-renders)
  const requestOtpInFlightRef = useRef(false);

  // util to compute remaining seconds from server epoch seconds
  const computeRemainingFromExpires = (expiresAtEpochSeconds) => {
    if (!expiresAtEpochSeconds) return null;
    const now = Math.floor(Date.now() / 1000);
    return Math.max(0, Number(expiresAtEpochSeconds) - now);
  };

  // Shared helper for calling requestOTP and handling server "otp already exists" case
  const tryRequestOtpAndHandle = async (payload) => {
    // Prevent duplicate submissions
    if (requestOtpInFlightRef.current || isSubmitting) {
      return { handled: true };
    }

    setReservationError('');
    setCaptchaError('');
    setIsSubmitting(true);
    requestOtpInFlightRef.current = true;

    try {
      // (email + issuerCode)
      const res = await listingCalendarService.requestOTP(payload.email, payload.issuerCode);

      // Expected success shape example: { ok: true, expires_at, ttl_seconds }
      const data = res && res.data ? res.data : {};

      let remaining = null;

      if (data.expires_at != null) {
        // Use expires_at if present (epoch seconds, server time)
        remaining = computeRemainingFromExpires(data.expires_at);
      } else if (typeof data.ttl_seconds === 'number') {
        // Remaining life of the current/just-issued OTP
        remaining = Math.max(0, Number(data.ttl_seconds));
      } else if (typeof data.remaining_seconds === 'number') {
        // Some backends return remaining_seconds on success
        remaining = Math.max(0, Number(data.remaining_seconds));
      } else if (typeof data.cooldownSeconds === 'number') {
        // camelCase variant
        remaining = Math.max(0, Number(data.cooldownSeconds));
      } else if (typeof data.cooldown_seconds === 'number') {
        // snake_case variant
        remaining = Math.max(0, Number(data.cooldown_seconds));
      }

      // Fallback only if the backend provided nothing; avoid guessing if we can compute precisely
      if (remaining == null) remaining = 120;

      setFormData({ ...payload });
      setPreviousModal('reservation');
      setOtpInitialCooldown(remaining);
      setOtpInitialError(''); // clear any prior OTP error on success
      setOtpInitialInfo('');
      setShowOtpModal(true);

      return { handled: true };
    } catch (err) {
      const data =
        (err && err.response && err.response.data) ? err.response.data :
        (err && err.data) ? err.data : null;

      // Special handling: existing OTP
      if (
        data &&
        (data.error && /otp already exists/i.test(String(data.error))) &&
        (data.remaining_seconds || data.remaining_seconds === 0)
      ) {
        const remaining = Math.max(0, Number(data.remaining_seconds) || 0);
        setFormData({ ...payload });
        setPreviousModal('reservation');
        setOtpInitialCooldown(remaining);
        setOtpInitialError('An OTP was already sent — please use the existing code or wait for the cooldown to expire.');
        setOtpInitialInfo(data.request_count ? `Already requested ${data.request_count} time(s).` : '');
        setShowOtpModal(true);
        return { handled: true };
      }

      // Generic error handling
      const messageFromServer = data && (data.message || data.error || JSON.stringify(data));
      const message = messageFromServer
        ? String(messageFromServer)
        : (err && err.message)
          ? err.message
          : 'Failed to request OTP. Please try again later.';

      if (isCaptchaFlowRef.current) {
        // show the error in both places so the user sees it after the CAPTCHA UI is removed
        setCaptchaError(message);
        setReservationError(message);
      } else {
        setReservationError(message);
      }
      return { handled: false, err };
    } finally {
      isCaptchaFlowRef.current = false;
      setIsSubmitting(false);
      requestOtpInFlightRef.current = false;
    }
  };

  const onCaptchaSuccess = async (wafToken) => {
    if (!pendingFormRef.current) return;
    const payload = pendingFormRef.current;
    pendingFormRef.current = null;

    isCaptchaFlowRef.current = true;
    await tryRequestOtpAndHandle(payload);
    // DOM cleanup is handled by the deferred setTimeout in use-captcha.js onSuccess
  };

  const onCaptchaFailure = (message = 'CAPTCHA verification failed. Please try again.') => {
    isCaptchaFlowRef.current = false;
    setCaptchaError(message);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      return;
    }

    if (showsecondaryListingDate) {
      if (!secondaryListingDate) {
        setSecondaryListingDateError('Please select the second date.');
        setShowCalendar2(true);
        return;
      }
      if (!primaryListingDate) {
        setSecondaryListingDateError('Please select the first date before choosing a second date.');
        setShowCalendar2(true);
        return;
      }
      if (secondaryListingDate <= primaryListingDate) {
        setSecondaryListingDateError('Secondary Listing Date must be after Primary Listing Date.');
        setShowCalendar2(true);
        return;
      }
    }

    setSecondaryListingDateError('');

    // Since we no longer fetch issuers, the institutionCode is whatever the user typed (issuerCode)
    const institutionCode = issuerCode || '';

    // issuerName is no longer pulled from a provider — leave blank or mirror issuerCode if desired
    const issuerName = '';

    const payload = {
      email,
      issuerCode,
      issuerName,
      institutionCode,
      primaryListingDate,
      secondaryListingDate: showsecondaryListingDate ? secondaryListingDate : ''
    };

    const captchaEnabled = true;

    if (!captchaEnabled) {
      await tryRequestOtpAndHandle(payload);
      return;
    }

    if (captchaToken) {
      await tryRequestOtpAndHandle(payload);
      return;
    }

    setCaptchaError('');
    setReservationError('');
    pendingFormRef.current = payload;
    setShowCaptcha(true);
    setTimeout(() => renderCaptcha(onCaptchaSuccess, onCaptchaFailure), 80);
  };

  const handleOtpConfirm = (otp) => {
    const base = formData || {
      email,
      issuerCode,
      primaryListingDate,
      secondaryListingDate: showsecondaryListingDate ? secondaryListingDate : '',
    };
    const payload = { ...base };
    setReservationPayloadForConfirm(payload);

    setPreviousModal('otp');
    setShowOtpModal(false);
    setShowConfirmModal(true);
  };

  const handleConfirmSuccess = (result) => {
    setShowConfirmModal(false);
    setReservationPayloadForConfirm(null);
    setFormData(null);
    setPreviousModal(null);
    onHideWrapper();
    if (typeof onConfirm === 'function') {
      onConfirm(result);
    }
  };

  const handleOtpHide = () => {
    // When user cancels/closes the OTP modal, clear any captcha tokens so
    // the next Request -> captcha will actually show again.
    try {
      if (captchaScriptUrl) cleanupCaptcha();
    } catch (e) {
      // ignore
    }
    setShowOtpModal(false);
    setPreviousModal(null);
  };

  const handleConfirmHide = () => {
    setShowConfirmModal(false);
    if (previousModal === 'otp') {
      setShowOtpModal(true);
    }
    setPreviousModal(null);
  };

  function onHideWrapper() {
    if (captchaScriptUrl) cleanupCaptcha();
    onHide();
  }

  const validateEmail = (value) => {
    const v = (value || '').trim();
    if (!v) {
      setEmailError('Email is required.');
      return false;
    }

    const basicEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!basicEmailRegex.test(v)) {
      setEmailError('Please enter a valid email address.');
      return false;
    }

    const lower = v.toLowerCase();
    const parts = lower.split('@');
    const domain = parts[1] || '';

    const blocked = nonInstitutionEmail() || [];

    const isBlocked = blocked.some((entry) => {
      let raw = '';
      if (!entry) return false;
      if (typeof entry === 'string') raw = entry;
      else if (typeof entry === 'object' && entry.value) raw = entry.value;
      const pat = (raw || '').toLowerCase().trim();
      if (!pat) return false;
      if (pat.startsWith('@')) {
        const p = pat.slice(1);
        return domain.startsWith(p);
      }
      if (pat.includes('.')) {
        return domain === pat || domain.endsWith(`.${pat}`) || domain.endsWith(pat);
      }
      return domain.includes(pat);
    });

    if (isBlocked) {
      setEmailError('Please use your institution email address (personal emails are not allowed).');
      return false;
    }

    setEmailError('');
    return true;
  };

  const showReservation = show && !showOtpModal && !showConfirmModal;

  return (
    <>
      <Modal show={showReservation} onHide={onHideWrapper} centered>
        <Form onSubmit={handleSubmit} autoComplete="off" spellCheck={false}>
          <Modal.Header closeButton>
            <Modal.Title>Make a Reservation</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {reservationError ? <Alert variant="danger">{reservationError}</Alert> : null}

            <Form.Group controlId="reservationEmail" className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => {
                  const val = e.target.value;
                  setEmail(val);
                  validateEmail(val);
                }}
                required
                isInvalid={!!emailError}
              />
              {emailError ? <Form.Control.Feedback type="invalid">{emailError}</Form.Control.Feedback> : null}
            </Form.Group>

            <Form.Group controlId="reservationIssuerCode" className="mb-3">
              <Form.Label>Issuer Code</Form.Label>

              <Form.Control
                type="text"
                placeholder="Enter institution code"
                value={issuerCode}
                onChange={(e) => setIssuerCode(e.target.value.toUpperCase())}
                onKeyPress={(e) => {
                  if (!/[a-zA-Z]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                required
                disabled={isSubmitting}
              />
            </Form.Group>

            <Form.Group controlId="reservationprimaryListingDate" className="mb-3">
              <Form.Label>Primary Listing Date</Form.Label>
              <div className="d-flex align-items-center">
                <Form.Control type="text" value={primaryListingDate || ''} readOnly />
              </div>
            </Form.Group>

            {showsecondaryListingDate && (
              <SecondaryListingDatePicker
                primaryListingDate={primaryListingDate}
                secondaryListingDate={secondaryListingDate}
                setSecondaryListingDate={setSecondaryListingDate}
                showCalendar2={showCalendar2}
                setShowCalendar2={setShowCalendar2}
                secondaryListingDateError={secondaryListingDateError}
                setSecondaryListingDateError={setSecondaryListingDateError}
                reservedDates={reservedDates}
              />
            )}

            {captchaScriptUrl && (
              <div style={{ display: showCaptcha ? undefined : 'none' }}>
                <div id="aws-waf-captcha-container" ref={captchaRef} aria-live="polite" />
                <div style={{ marginTop: 6, fontSize: 13 }}>
                  <small>Please solve the CAPTCHA to continue your reservation.</small>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => {
                      pendingFormRef.current = null;
                      isCaptchaFlowRef.current = false;
                      cleanupCaptcha();
                      setShowCaptcha(false);
                      setCaptchaError('CAPTCHA cancelled.');
                    }}
                    style={{ marginLeft: 8 }}
                  >
                    Cancel
                  </Button>
                </div>

                {captchaError ? <div className="text-danger small" style={{ marginTop: 8 }}>{captchaError}</div> : null}
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={onHideWrapper} type="button" disabled={isSubmitting || showCaptcha}>
              Cancel
            </Button>

            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting || showCaptcha || !!emailError || !email}
            >
              { (isSubmitting || showCaptcha) ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Requesting...
                </>
              ) : 'Request for Reservation'}
            </Button>

          </Modal.Footer>
        </Form>
      </Modal>

      <OTPFormModal
        show={showOtpModal}
        onHide={handleOtpHide}
        email={(formData && formData.email) || email}
        issuerCode={(formData && formData.issuerCode) || issuerCode}
        issuerName={(formData && formData.issuerName) || ''}
        onConfirm={handleOtpConfirm}
        initialCooldown={otpInitialCooldown}
        initialError={otpInitialError}
        initialInfo={otpInitialInfo}
      />

      <ConfirmReserveModal
        show={showConfirmModal}
        onHide={handleConfirmHide}
        reservationData={reservationPayloadForConfirm}
        onSuccess={handleConfirmSuccess}
      />
    </>
  );
};

export const ResevationFormQueryClientProvider = (props) => (
  <ReservationModal {...props} />
);

export default ResevationFormQueryClientProvider;